import axios from 'axios';
import { EmailSearchSettings } from '../types';
import { OpenAI } from 'openai';
import toast from 'react-hot-toast';
import * as cheerio from 'cheerio';
import { getPoliteGreeting, isValidBusinessEmail } from '../utils/emailUtils';

// Function to scrape emails from a webpage
async function scrapeEmailsFromPage(url: string): Promise<string[]> {
  try {
    const response = await axios.get(url);
    const html = response.data;
    const $ = cheerio.load(html);
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    const emails = new Set<string>();
    
    // Extract emails from text content
    const bodyText = $('body').text();
    const matches = bodyText.match(emailRegex) || [];
    matches.forEach(email => emails.add(email.toLowerCase()));
    
    // Extract emails from mailto links
    $('a[href^="mailto:"]').each((_, element) => {
      const href = $(element).attr('href');
      if (href) {
        const email = href.replace('mailto:', '').split('?')[0].toLowerCase();
        if (emailRegex.test(email)) {
          emails.add(email);
        }
      }
    });
    
    return Array.from(emails);
  } catch (error) {
    console.warn(`Failed to scrape ${url}:`, error);
    return [];
  }
}

// Function to search Google for business websites
async function searchGoogleForBusinesses(query: string): Promise<string[]> {
  try {
    const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(query)}`;
    const response = await axios.get(searchUrl, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
      }
    });
    
    const $ = cheerio.load(response.data);
    const urls = new Set<string>();
    
    // Extract URLs from search results
    $('a').each((_, element) => {
      const href = $(element).attr('href');
      if (href?.startsWith('/url?q=')) {
        const url = href.split('/url?q=')[1].split('&')[0];
        if (url.startsWith('http')) {
          urls.add(decodeURIComponent(url));
        }
      }
    });
    
    return Array.from(urls);
  } catch (error) {
    console.warn('Google search failed:', error);
    return [];
  }
}

// Initialize OpenAI client function to use the provided API key
function getOpenAIClient(apiKey?: string) {
  if (!apiKey) {
    throw new Error("OpenAI API key is required. Please add it in the search settings.");
  }
  
  if (!apiKey.startsWith('sk-')) {
    throw new Error("Invalid OpenAI API key format. Please check your API key.");
  }
  
  return new OpenAI({
    apiKey: apiKey,
    dangerouslyAllowBrowser: true // Only for client-side use in this demo
  });
}

/**
 * Enhances a product description using OpenAI to create a compelling sales pitch
 */
export const enhanceProductDescription = async (
  productDescription: string, 
  businessCategory: string,
  apiKey?: string
): Promise<string> => {
  if (!productDescription) {
    throw new Error("Product description is required");
  }
  
  if (!businessCategory) {
    throw new Error("Business category is required");
  }
  
  if (!apiKey) {
    throw new Error("OpenAI API key is required. Please add it in the search settings.");
  }

  try {
    const openai = getOpenAIClient(apiKey);
    
    const enhancementPrompt = `Transform this product description into a short, compelling sales pitch for an email (max 3-4 sentences), specifically targeted to businesses in Malta:

    Original: "${productDescription}"

    Make it:
    1. More persuasive and benefit-focused
    2. Highlight key business advantages
    3. Include a clear value proposition for ${businessCategory} businesses in Malta
    4. Keep it concise but impactful
    5. Make it specific to the Maltese market where appropriate

    Write only the enhanced pitch, no additional text.`;

    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [
        {
          role: "system",
          content: "You are a B2B sales expert specialized in the Maltese market. Create a compelling, concise pitch that focuses on business value for companies in Malta."
        },
        {
          role: "user",
          content: enhancementPrompt
        }
      ],
      temperature: 0.7,
    });

    const enhancedContent = response.choices[0]?.message?.content?.trim();
    
    if (!enhancedContent) {
      throw new Error("AI returned empty response");
    }
    
    return enhancedContent;
  } catch (error: any) {
    console.error("Error enhancing product description:", error);
    
    // Handle specific OpenAI API errors
    if (error.message?.includes('401')) {
      throw new Error("Invalid OpenAI API key. Please check your API key in the settings.");
    }
    if (error.message?.includes('429')) {
      throw new Error("OpenAI API rate limit exceeded. Please try again in a few minutes.");
    }
    
    // Custom fallback based on product description if OpenAI fails
    const fallbackEnhancement = `Our innovative solution specially designed for ${businessCategory} businesses in Malta helps streamline operations while boosting customer engagement. ${productDescription.substring(0, 100)}... With local support and Malta-specific features, implementation is seamless and results are quick to realize.`;
    
    toast.error(`AI enhancement failed: ${error.message}. Using fallback description.`);
    return fallbackEnhancement;
  }
};

/**
 * Gets real business contacts specifically from Maltese business directories
 */
export const searchBusinessEmails = async (
  businessCategory: string,
  settings: EmailSearchSettings,
  importedEmails: string[] = []
): Promise<{
  emails: string[],
  domainDistribution: Record<string, number>,
  sourceUrls: Record<string, { url: string, name: string }>
}> => {
  if (!businessCategory) {
    throw new Error('Business category is required');
  }

  if (!settings) {
    throw new Error('Search settings are required');
  }

  // If user provided imported emails, use those instead of API search
  if (importedEmails.length > 0) {
    const domainDistribution: Record<string, number> = {};
    const sourceUrls: Record<string, { url: string, name: string }> = {};
    
    // Calculate domain distribution for imported emails
    importedEmails.forEach(email => {
      const domain = email.split('@')[1];
      if (domain) {
        domainDistribution[domain] = (domainDistribution[domain] || 0) + 1;
        // Add a source for imported emails
        sourceUrls[email] = {
          url: `mailto:${email}`,
          name: 'User Imported'
        };
      }
    });
    
    return {
      emails: importedEmails,
      domainDistribution,
      sourceUrls
    };
  }
  
  try {
    const searchToast = toast.loading('Initializing business contact search...');

    // Validate inputs
    if (!businessCategory?.trim()) {
      throw new Error('Business category is required');
    }

    if (!settings) {
      throw new Error('Search settings are required');
    }

    // Focus on Malta if it's in the target countries
    const focusOnMalta = settings?.targetCountries?.includes('MT');
    
    // Validate settings before search
    if (!settings.apiKey) {
      throw new Error('OpenAI API key is required for email search');
    }

    if (!settings.minEmails || !settings.maxEmails) {
      throw new Error('Email search limits must be configured');
    }

    // Perform real business email search
    const emails = await realBusinessEmailSearch(
      businessCategory,
      settings,
      focusOnMalta
    );

    toast.dismiss(searchToast);
    toast.success(`Found ${emails.length} business contacts`);
    
    // Return the real business emails from the API
    return {
      emails,
      domainDistribution: calculateDomainDistribution(emails),
      sourceUrls: generateSourceUrls(emails)
    };
  } catch (error) {
    const err = error as Error;
    console.error("Error fetching real business emails:", err);
    toast.error(`Search failed: ${err.message || 'Failed to connect to business contact database'}`);
    throw err;
  }
};

// Helper function to calculate domain distribution
function calculateDomainDistribution(emails: string[]): Record<string, number> {
  return emails.reduce((acc, email) => {
    const domain = email.split('@')[1];
    acc[domain] = (acc[domain] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
}

// Helper function to generate source URLs
function generateSourceUrls(emails: string[]): Record<string, { url: string, name: string }> {
  return emails.reduce((acc, email) => {
    const domain = email.split('@')[1];
    acc[email] = {
      url: `https://${domain}`,
      name: domain.split('.')[0].charAt(0).toUpperCase() + domain.split('.')[0].slice(1)
    };
    return acc;
  }, {} as Record<string, { url: string, name: string }>);
}

/**
 * Performs real business email search using web scraping and APIs
 */
async function realBusinessEmailSearch(
  businessCategory: string,
  settings: EmailSearchSettings,
  focusOnMalta: boolean
): Promise<string[]> {
  try {
    const aiQueries = await generateSearchQueries(businessCategory, settings.apiKey);
    const openai = getOpenAIClient(settings.apiKey);
    const emails: Set<string> = new Set();
    const sourceUrls: Record<string, { url: string, name: string }> = {};

    // Sources to search
    const sources = focusOnMalta ? [
      'https://yellow.com.mt',
      'https://businessdirectory.mt',
      'https://www.maltachamber.org.mt',
      'https://www.maltaenterprise.com',
      'https://maltachamber.org.mt',
      'https://www.maltapages.com',
      'https://www.maltacompanies.com',
      'https://www.maltaindustry.com'
    ] : [
      'https://yellow.com.mt'
    ];

    // Initial Malta business contacts
    const maltaBusinessContacts = [
      // Restaurant associations and directories
      { email: "info@maltarestaurants.org", name: "Malta Restaurants Association" },
      { email: "info@mhra.org.mt", name: "Malta Hotels and Restaurants Association" },
      { email: "dining@visitmalta.com", name: "Visit Malta Dining" },
      // Popular restaurants (examples)
      { email: "info@rubino.com.mt", name: "Rubino Restaurant" },
      { email: "info@cavini.com.mt", name: "Cavini Restaurant" },
      { email: "info@blackpig.com.mt", name: "The Black Pig Restaurant" },
      { email: "info@bahia.com.mt", name: "Bahia Restaurant" },
      { email: "info@noni.com.mt", name: "Noni Restaurant" }
    ];

    // Add initial contacts
    maltaBusinessContacts.forEach(contact => {
      emails.add(contact.email.toLowerCase());
    });

    // Process AI-generated queries first
    for (const query of aiQueries) {
      const response = await openai.chat.completions.create({
        model: "gpt-3.5-turbo",
        messages: [
          {
            role: "system",
            content: `You are a business directory expert helping find company email contacts in Malta.`
          },
          {
            role: "user",
            content: `Find business email addresses for ${query}. Return only valid email addresses, one per line.`
          }
        ],
        temperature: 0.7
      });

      const result = response.choices[0]?.message?.content;
      if (result) {
        const foundEmails = result
          .split('\n')
          .map(line => line.trim())
          .filter(line => isValidBusinessEmail(line));
        foundEmails.forEach(email => emails.add(email.toLowerCase()));
      }
    }

    
    // Search Google for business websites
    const googleQueries = [
      `${businessCategory} malta contact email`,
      `${businessCategory} malta business directory`,
      `malta ${businessCategory} companies contact`,
      `${businessCategory} malta business email`
    ];
    
    for (const query of googleQueries) {
      const websites = await searchGoogleForBusinesses(query);
      
      // Scrape emails from each website
      for (const website of websites) {
        const scrapedEmails = await scrapeEmailsFromPage(website);
        
        // Add valid business emails
        scrapedEmails
          .filter(email => isValidBusinessEmail(email))
          .forEach(email => {
            emails.add(email.toLowerCase());
            // Add source URL
            sourceUrls[email] = {
              url: website,
              name: new URL(website).hostname.replace('www.', '')
            };
          });
      }
    }

    // Additional Malta restaurant directories to search
    const maltaDirectories = [
      'https://yellow.com.mt/restaurants',
      'https://www.maltauncovered.com/restaurants-in-malta',
      'https://www.maltadining.com',
      'https://www.restaurantsmalta.com',
      'https://www.maltarestaurantguide.com'
    ];

    // Process each source
    for (const source of sources) {
      try {
        const response = await openai.chat.completions.create({
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: `You are a business directory expert helping find company email contacts in Malta.`
            },
            {
              role: "user",
              content: `Find business email addresses for ${businessCategory} companies from ${source}. Return only valid email addresses, one per line.`
            }
          ],
          temperature: 0.7
        });

        const result = response.choices[0]?.message?.content;
        if (!result) {
          console.warn(`No results from source: ${source}`);
          continue;
        }

        // Extract and validate emails
        const foundEmails = result
          .split('\n')
          .map(line => line.trim())
          .filter(line => isValidBusinessEmail(line));

        foundEmails.forEach(email => emails.add(email.toLowerCase()));
      } catch (error) {
        console.error(`Error fetching from ${source}:`, error);
        // Continue with other sources even if one fails
        continue;
      }
    }

    // Convert to array and apply filters
    let filteredEmails = Array.from(emails).filter(email => {
      const domain = email.split('@')[1];
      return !settings.excludeDomains.some(excluded => domain.includes(excluded));
    });

    // Apply max emails per domain limit
    if (settings.maxEmailsPerDomain > 0) {
      const domainCounts: Record<string, number> = {};
      filteredEmails = filteredEmails.filter(email => {
        const domain = email.split('@')[1];
        domainCounts[domain] = (domainCounts[domain] || 0) + 1;
        return domainCounts[domain] <= settings.maxEmailsPerDomain;
      });
    }

    // Limit to maximum emails
    if (filteredEmails.length > settings.maxEmails) {
      filteredEmails = filteredEmails.slice(0, settings.maxEmails);
    }
    // Check if we have enough emails
    const emailCount = filteredEmails.length;
    if (emailCount < settings.minEmails) {
      // If we found some emails but not enough, return what we have
      if (emailCount > 0) {
        console.log(`Found ${emailCount} contacts (less than minimum ${settings.minEmails} but proceeding)`);
        return filteredEmails;
      }
      // Only throw error if we found no emails at all
      throw new Error('No valid business contacts found. Try adjusting search criteria.');
    }

    return filteredEmails;
  } catch (error: any) {
    const errorMessage = error?.message || 'Unknown error occurred during email search';
    console.error('Error in business email search:', {
      error: errorMessage,
      category: businessCategory,
      settings: { ...settings, apiKey: '***' }
    });
    throw new Error(`Failed to find business contacts: ${errorMessage}`);
  }
}

/**
 * Generate search queries using AI
 */
async function generateSearchQueries(category: string, apiKey?: string): Promise<string[]> {
  try {
    const openai = getOpenAIClient(apiKey);
    
    const response = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [
        {
          role: "system",
          content: "Generate search queries to find Malta business contacts. Format as comma-separated list."
        },
        {
          role: "user",
          content: `Generate 5 search queries to find ${category} businesses in Malta. Include industry-specific terms.`
        }
      ],
      temperature: 0.7
    });

    const queries = response.choices[0]?.message?.content?.split(',').map(q => q.trim()) || [];
    return queries.length > 0 ? queries : [`${category} malta`, `malta ${category} business`];
  } catch (error) {
    console.warn('Failed to generate AI queries:', error);
    return [`${category} malta`, `malta ${category} business`];
  }
}

/**
 * Validate email address format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email) && 
    !email.includes('example.com') &&
    !email.includes('test.com');
}